import React from "react";
import styled from "styled-components";

import { SCORING_DEFAULTS } from "../../scoring";

const KNOBS = [
  {
    key: "largeMassFraction",
    label: "mass fraction (large)",
    min: 0.05,
    max: 0.95,
    step: 0.01,
    format: (v) => v.toFixed(2),
  },
  {
    key: "smallMassFraction",
    label: "mass fraction (small)",
    min: 0.02,
    max: 0.8,
    step: 0.01,
    format: (v) => v.toFixed(2),
  },
  {
    key: "searchRadiusScale",
    label: "search radius ×",
    min: 0.1,
    max: 3,
    step: 0.05,
    format: (v) => v.toFixed(2),
  },
  {
    key: "smallMaxOverdrawRatio",
    label: "max overdraw × (small)",
    min: 2,
    max: 50,
    step: 0.5,
    format: (v) => v.toFixed(1),
  },
  {
    key: "largeMaxOverdrawRatio",
    label: "max overdraw × (large)",
    min: 1.2,
    max: 10,
    step: 0.1,
    format: (v) => v.toFixed(1),
  },
  {
    key: "distanceSoftener",
    label: "distance softener (px)",
    min: 20,
    max: 500,
    step: 5,
    format: (v) => String(Math.round(v)),
  },
  {
    key: "detectionFillMs",
    label: "bar fill rate · fill (ms)",
    min: 50,
    max: 1500,
    step: 25,
    format: (v) => String(Math.round(v)),
  },
  {
    key: "detectionStrokeMs",
    label: "bar fill rate · stroke (ms)",
    min: 200,
    max: 3000,
    step: 50,
    format: (v) => String(Math.round(v)),
  },
];

function DebugScoringPanel({
  overrides,
  onChange,
  shown,
  maskOverlay,
  onToggleMask,
  marcId,
}) {
  const reset = () => onChange(SCORING_DEFAULTS);
  const dirty = KNOBS.some((k) => overrides[k.key] !== SCORING_DEFAULTS[k.key]);

  return (
    <Wrap>
      <Header>
        <span>scoring knobs</span>
        {marcId && <MarcId>marc {marcId}</MarcId>}
        <Reset onClick={reset} disabled={!dirty}>
          reset
        </Reset>
      </Header>
      {KNOBS.map((k) => (
        <Row key={k.key}>
          <RowHeader>
            <Label>{k.label}</Label>
            <Value>{k.format(overrides[k.key])}</Value>
          </RowHeader>
          <input
            type="range"
            min={k.min}
            max={k.max}
            step={k.step}
            value={overrides[k.key]}
            onChange={(e) =>
              onChange({ ...overrides, [k.key]: Number(e.target.value) })
            }
          />
          <Default>default {k.format(SCORING_DEFAULTS[k.key])}</Default>
        </Row>
      ))}
      <ShownSection
        shown={shown}
        maskOverlay={maskOverlay}
        onToggleMask={onToggleMask}
      />
    </Wrap>
  );
}

// Live debug readout for the currently-graded region. Shows the math
// behind whatever the bar is doing right now, plus per-subregion
// breakdown for any-of groups (so e.g. "the small shirt sliver got
// 200 hits, the big half got 6K" reads at a glance).
// Show the breakdown when there's anything interesting to break down:
// multiple subregions, or any single sub that uses spread split.
function shouldShowSubBreakdown(subRegions) {
  if (!subRegions) return false;
  if (subRegions.length > 1) return true;
  return subRegions.some((s) => s.splitAxis);
}

function ShownSection({ shown, maskOverlay, onToggleMask }) {
  return (
    <Shown>
      <Header>
        <span>shown region</span>
        <Reset onClick={onToggleMask}>
          {maskOverlay ? "hide mask" : "show mask"}
        </Reset>
      </Header>
      {!shown ? (
        <Empty>none</Empty>
      ) : (
        <>
          <StatRow>
            <Label>label</Label>
            <Value>
              {shown.label} ({shown.mode})
            </Value>
          </StatRow>
          <StatRow>
            <Label>hits / required</Label>
            <Value>
              {Math.round(shown.hits)} / {shown.required}
            </Value>
          </StatRow>
          <StatRow>
            <Label>score</Label>
            <Value>{shown.score.toFixed(2)}</Value>
          </StatRow>
          <StatRow>
            <Label>ref pixelCount</Label>
            <Value>{shown.pixelCount}</Value>
          </StatRow>
          <StatRow>
            <Label>assignedSize</Label>
            <Value>
              {shown.assignedSize} / {shown.overdrawAllowed}
              {shown.overdrawn ? " ⚠" : ""}
            </Value>
          </StatRow>
          {shown.subRegions && shouldShowSubBreakdown(shown.subRegions) && (
            <SubBreakdown>
              {shown.subRegions.map((s) => (
                <SubRowGroup key={s.id}>
                  <SubRow>
                    <span>{s.id}</span>
                    <span>
                      {s.pixelCount}px · {Math.round(s.hits)} hits ·{" "}
                      {s.assignedSize} size
                    </span>
                  </SubRow>
                  {s.splitAxis && s.halfHits && s.halfThresholds && (
                    <SubRow>
                      <span>{s.splitAxis === "x" ? "  l/r" : "  t/b"}</span>
                      <span>
                        {Math.round(s.halfHits[0])}/{Math.round(s.halfHits[1])}{" "}
                        of {Math.round(s.halfThresholds[0])}/
                        {Math.round(s.halfThresholds[1])}
                      </span>
                    </SubRow>
                  )}
                </SubRowGroup>
              ))}
            </SubBreakdown>
          )}
        </>
      )}
    </Shown>
  );
}

const Wrap = styled.div`
  position: fixed;
  top: 12px;
  right: 12px;
  z-index: 50;
  width: 220px;
  padding: 10px 12px;
  background: rgba(26, 26, 26, 0.88);
  color: #f6f5f1;
  border-radius: 8px;
  font-size: 11px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 10px;
  opacity: 0.85;
`;

const Reset = styled.button`
  background: transparent;
  color: inherit;
  border: 1px solid #666;
  border-radius: 4px;
  padding: 2px 8px;
  font: inherit;
  font-size: 10px;
  cursor: pointer;
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
  &:not(:disabled):hover {
    background: #333;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  input[type="range"] {
    width: 100%;
  }
`;

const RowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

const Label = styled.span`
  opacity: 0.85;
`;

const Value = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 700;
`;

const Default = styled.span`
  font-size: 10px;
  opacity: 0.45;
`;

const Shown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding-top: 8px;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
`;

const SubBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
  padding-top: 4px;
  border-top: 1px dashed rgba(255, 255, 255, 0.12);
  font-size: 10px;
  opacity: 0.7;
  font-variant-numeric: tabular-nums;
`;

const SubRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const SubRowGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Empty = styled.span`
  opacity: 0.45;
`;

const MarcId = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--color-orange-300, #fdba74);
  font-size: 10px;
`;

export default DebugScoringPanel;
